/* A chapter, read as a document: the whole path on the left for orientation, a
   measure-limited reading column in the middle, and the stepper pinned under it.
   The graph is a separate mode, so this is the only view that scrolls. */
import { useTranslation } from 'react-i18next';
import { GOAL_NODES } from '../../data/goals';
import {
  chapterDone,
  children,
  curBlock,
  pad,
  progress as chapterProgress,
  stateOf,
  unlockNeighbours,
  xpOf,
} from '../../engine/progress';
import { useApp } from '../../state/AppProvider';
import { useGameStage } from '../../state/useGame';
import { BlockView } from '../blocks/BlockView';
import { Outline } from './Outline';
import { Stepper } from './Stepper';
import type { NodeDef } from '../../types';

export function Chapter({ node }: { node: NodeDef }) {
  const { t } = useTranslation();
  const { progress, nextBlock, prevBlock, finishChapter, selectNode } = useApp();

  const index = curBlock(node, progress);
  const block = node.blocks[index];
  const state = stateOf(node, progress);
  const last = index === node.blocks.length - 1;
  const nextChapters = unlockNeighbours(node, progress);
  const hasGoal = GOAL_NODES.includes(node.id);

  /* An optional stage hanging off a chapter that is already cleared, offered
     once, in place. */
  const offer =
    !node.extra && state === 'done'
      ? (children(node.id).find((child) => child.extra && !chapterDone(child, progress)) ?? null)
      : null;

  /* Only the bonus stage has a board; asking for it here keeps the footer's
     "New game" button and the board itself on one state. */
  const game = useGameStage(block.kind === 'game' ? block : null);

  return (
    <div className="lesson">
      <nav className="lesson-rail" aria-label={t('outline')}>
        <Outline node={node} />
        <div className="rail-group">
          <p className="eyebrow">{t('thisChapter')}</p>
          <Stepper node={node} />
        </div>
      </nav>

      <div className="lesson-main">
        <header className="lesson-head" data-od-id="lesson-head">
          <div>
            <div className="sym">
              <span className="node-idx num">{pad(node)}</span>
              <span className="node-tag">
                {node.bonus ? t('legendBonus') : node.extra ? t('optional') : t(node.tagKey)}
              </span>
              <span className="meta num">
                {chapterProgress(node, progress)}/{node.blocks.length} · {xpOf(node)} {t('xp')}
              </span>
            </div>
            <h1 className="h1">{t(node.titleKey)}</h1>
            <p className="lead" style={{ marginTop: 8, maxWidth: '44ch' }}>
              {t(node.subKey)}
            </p>
            {hasGoal && (
              <p className="goal">
                <span className="k">{t('goalLabel')}</span>
                {t(`path:${node.id}.goal`)}
              </p>
            )}
          </div>
          <button type="button" className="btn btn-secondary btn-sm" data-act="overview" onClick={() => selectNode(null)}>
            {t('backToMap')}
          </button>
        </header>

        <div className="reader" data-od-id="reader">
          {state === 'done' && (
            <div className="banner">
              <span>
                {t('completed')} · {xpOf(node)} {t('xpEarned')}
              </span>
              {nextChapters.length ? (
                <button
                  type="button"
                  className="btn btn-ghost arrow"
                  data-act="node"
                  data-id={nextChapters[0].id}
                  onClick={() => selectNode(nextChapters[0].id)}
                >
                  {t('nextChapter')}
                </button>
              ) : (
                <span className="meta num">✓</span>
              )}
            </div>
          )}

          {offer && (
            <div className="offer" data-od-id="deep-dive-offer">
              <div className="offer-main">
                <p className="eyebrow">{t('deeperTitle')}</p>
                <p className="offer-title">{t(offer.titleKey)}</p>
                <p className="meta">
                  {t('deeperLead')} {xpOf(offer)} {t('xp')} · {offer.blocks.length} {t('blocksOf')}
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary arrow"
                data-act="node"
                data-id={offer.id}
                onClick={() => selectNode(offer.id)}
              >
                {t('deeperCta')}
              </button>
            </div>
          )}

          <BlockView node={node} block={block} index={index} />

          {block.kind === 'check' && !progress.chk[block.id] && <p className="meta">{t('passHint')}</p>}
        </div>

        <div className="lesson-foot">
          <button
            type="button"
            className="btn btn-ghost arrow-l"
            disabled={index === 0}
            data-act="prev-block"
            data-id={node.id}
            onClick={() => prevBlock(node.id)}
          >
            {t('blockOf')} {('0' + index).slice(-2)}
          </button>

          {block.kind === 'game' ? (
            progress.done[block.id] ? (
              <button type="button" className="btn btn-primary arrow" data-act="overview" onClick={() => selectNode(null)}>
                {t('backToMap')}
              </button>
            ) : (
              <button type="button" className="btn btn-secondary" data-act="g-reset" onClick={game.reset}>
                {t('gameRestart')}
              </button>
            )
          ) : block.kind === 'check' ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={!progress.chk[block.id]}
              data-act="finish"
              data-id={node.id}
              onClick={() => finishChapter(node.id)}
            >
              {last ? t('complete') : `${t('blockOf')} ${('0' + (index + 2)).slice(-2)}`} · +{block.xp} XP
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary arrow"
              data-act="next-block"
              data-id={node.id}
              data-i={index}
              onClick={() => nextBlock(node.id)}
            >
              {t('blockOf')} {('0' + (index + 2)).slice(-2)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
